import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';

import { User } from '../user/user.models';

interface ZipyApi {
  identify(uid: string, userInfo?: Record<string, string>): void;
  anonymize(): void;
  logError?(error: unknown, attrs?: Record<string, string>): void;
}

/**
 * Thin wrapper around the global `window.zipy` object injected by
 * the Zipy.ai SDK script in `index.prod.html`.
 *
 * All calls are no-ops when the script is absent (e.g. local dev builds).
 */
@Injectable({ providedIn: 'root' })
export class ZipyService {
  private readonly win = inject(DOCUMENT).defaultView;

  /**
   * Associate the current Zipy session with an authenticated user.
   *
   * @see https://docs.zipy.ai/configure/identifying-users
   */
  identify(user: User): void {
    const z = this.zipy();
    if (!z) return;

    z.identify(String(user.user_id), {
      email: user.email,
      name: user.name ?? 'na',
      customerName: user.name ?? 'na',
      organization: user.organization_name ?? 'na',
      phone: user.phone_number ?? 'na',
    });
  }

  /** Anonymize the current session (e.g. on sign-out). */
  anonymize(): void {
    this.zipy()?.anonymize();
  }

  /** Forward an error to Zipy for session replay correlation. */
  logError(error: unknown): void {
    this.zipy()?.logError?.(error);
  }

  private zipy(): ZipyApi | null {
    const w = this.win as (Window & { zipy?: ZipyApi }) | null;
    return w?.zipy && typeof w.zipy.identify === 'function' ? w.zipy : null;
  }
}
